import { Component, Input, Output, EventEmitter } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { map } from 'rxjs/operators';

import { RelatedConceptsModel } from '../../models/related-concepts.model';
import { MonitoringKeywordGroupModel } from '../../models/monitoring-keyword-group.model';
import { MercuryApi } from '../../../../api/mercury/mercury-api';
import { RelatedConceptsSearchModel } from '../../models/related-concepts-search.model';
import { operatorsEnum } from '../../enums/operators.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

@Component({
    selector: 'agpr-related-concepts',
    templateUrl: './related-concepts.component.html',
    styleUrls: ['./related-concepts.component.scss'],
    animations: [
        trigger('containerState', [
            state('hidden', style({
                height: '0',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
        ]),
    ],
    standalone: false
})
export class RelatedConceptsComponent {

    @Input() index: number;
    @Input() set keywordGroup(keyword: MonitoringKeywordGroupModel) {
        this.keywords = keyword;

        if (this.concepts) {
            this.concepts.updateConcepts(this.keywords);
        }

        this.setExpandable();
        this.setRefreshable();
    }

    @Output() updateKeywords = new EventEmitter<MonitoringKeywordGroupModel>();

    concepts: RelatedConceptsModel;
    collapsed: boolean = true;
    expandable: boolean = false;

    disableReload: boolean = true;
    disableConfirm: boolean = true;

    loading: boolean;

    private keywords: MonitoringKeywordGroupModel;
    private cachedKeywords: MonitoringKeywordGroupModel;

    readonly id = 'relatedConcepts';
    readonly buttonTypes = ButtonTypesEnum;
    readonly operatorOptions: DropdownOptionModel<operatorsEnum>[] = [
        {
            label: 'AND',
            value: operatorsEnum.and
        },
        {
            label: 'OR',
            value: operatorsEnum.or
        },
        {
            label: 'NOT',
            value: operatorsEnum.not
        }
    ];

    constructor (
        private mercuryApi: MercuryApi,
    ) {}

    confirm() {
        const keywords = this.concepts.convertToKeywordGroup();
        this.updateKeywords.emit(keywords);

        this.concepts.disableSelectedConcepts();

        this.disableReload = false;
        this.disableConfirm = true;
    }

    /**
    * Reloads the data in the component
    */
    reload() {
        this.getConcepts();
        this.cachedKeywords = this.keywords.clone();
        this.disableReload = true;
    }

    /**
    * Toggles the panel open or closed
    * based on its current state
    */
    togglePanel() {
        this.collapsed = !this.collapsed;

        this.disableReload = true;
        this.disableConfirm = true;

        if (!this.collapsed) {
            this.getConcepts();
            this.cachedKeywords = this.keywords.clone();
        }
    }

    /**
    * Gets a set of concepts based on the keywords provided to the component and stores them
    */
    private getConcepts() {
        this.loading = true;
        const search = new RelatedConceptsSearchModel(this.keywords, 'en');

        this.mercuryApi.relatedConcepts(search)
            .pipe(map(apiModel => new RelatedConceptsModel(apiModel)))
            .subscribe({
                next: relatedConcepts => {
                    this.concepts = relatedConcepts;
                    this.collapsed = false;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                }
            });
    }

    /**
    * Sets the value for expandable based on keywords.
    * If not expandable, collapse related concepts
    */
    private setExpandable() {
        if (!this.keywords) {
            return;
        }

        this.expandable = this.keywords.hasAnyKeywords();

        if (!this.expandable && !this.collapsed) {
            this.collapsed = true;
        }
    }

    /**
    * Sets the refreshable to true if cachedKeywords are not equal to keywords
    */
    private setRefreshable() {
        if (!this.keywords || !this.cachedKeywords) {
            return;
        }

        this.disableReload = this.keywords.equals(this.cachedKeywords);
    }

    setConfirmable() {
        this.disableConfirm = !this.concepts.anyConceptSelected();
    }
}
